'use client';

import React from 'react';
import { Timer, MousePointerClick, Target } from 'lucide-react';
import { formatTime } from '@/lib/gameLogic';

type Props = {
  timeSeconds: number;
  moves: number;
  pairsMatched: number;
  totalPairs: number;
  isRunning: boolean;
};

export default function StatsBar({ timeSeconds, moves, pairsMatched, totalPairs, isRunning }: Props) {
  const progressPct = (pairsMatched / totalPairs) * 100;

  return (
    <div className="flex flex-col gap-1.5">
      {/* Stats row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* Timer */}
        <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg px-3 py-1.5 min-w-[100px]">
          <Timer size={12} className={`${isRunning ? 'text-primary' : 'text-muted-foreground'}`} />
          <div>
            <p className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">Time</p>
            <p className={`font-mono text-base font-bold tabular-nums ${isRunning ? 'text-foreground' : 'text-muted-foreground'}`}>
              {formatTime(timeSeconds)}
            </p>
          </div>
          {isRunning && (
            <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse ml-1" />
          )}
        </div>

        {/* Moves */}
        <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg px-3 py-1.5 min-w-[80px]">
          <MousePointerClick size={12} className="text-muted-foreground" />
          <div>
            <p className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">Moves</p>
            <p className="font-mono text-base font-bold tabular-nums text-foreground">{moves}</p>
          </div>
        </div>

        {/* Pairs */}
        <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg px-3 py-1.5 min-w-[100px]">
          <Target size={12} className="text-muted-foreground" />
          <div>
            <p className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">Matched</p>
            <p className="font-mono text-base font-bold tabular-nums text-foreground">
              <span className="text-success">{pairsMatched}</span>
              <span className="text-muted-foreground text-xs"> / {totalPairs}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </div>
  );
}